import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Newsletter from '../components/Newsletter'
import Faq from '../components/Faq'
import '../styles/global.css'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas-pro'

const ComparisonResultsPage = () => {
  const navigate = useNavigate()

  // State for comparison data
  const [codes, setCodes] = useState([])
  const [codeDetails, setCodeDetails] = useState([])
  const [codeType, setCodeType] = useState('cnae')

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  // State to track if comparison has been performed
  const [comparisonPerformed, setComparisonPerformed] = useState(false)

  // Reference for the table of results
  const tableRef = useRef(null)

  // FAQ data for the page
  const faqData = [
    {
      question: '¿Qué es el comparador de códigos?',
      answer:
        'El comparador de códigos es una herramienta que te permite comparar lado a lado hasta tres códigos del mismo tipo (CNAE 2025, CNAE 2009 o IAE). Esta comparación te ayuda a identificar similitudes y diferencias entre actividades económicas similares, facilitando la toma de decisiones sobre la clasificación más adecuada para tu negocio.',
    },
    {
      question: '¿Cómo puedo comparar códigos?',
      answer:
        'Para comparar códigos, primero selecciona el tipo de código (CNAE 2025, CNAE 2009 o IAE) que deseas comparar. Luego, utiliza las barras de búsqueda para encontrar y seleccionar hasta tres códigos que quieres comparar. Una vez seleccionados al menos dos códigos, haz clic en el botón "Comparar" para ver el análisis detallado lado a lado.',
    },
    {
      question: '¿Puedo comparar códigos de diferentes tipos?',
      answer:
        'No, esta herramienta está diseñada para comparar códigos del mismo tipo. Si deseas ver la correspondencia entre diferentes tipos de códigos (por ejemplo, entre CNAE 2025 y CNAE 2009, o entre CNAE e IAE), te recomendamos utilizar nuestra herramienta de correspondencia disponible en las páginas de detalles de cada código.',
    },
    {
      question: '¿Qué información se muestra en la comparación?',
      answer:
        'La comparación muestra todos los detalles relevantes de los códigos seleccionados lado a lado, incluyendo: código, título, descripción detallada, categoría, nivel, notas (si existen) y palabras clave. Esto te permite identificar fácilmente las similitudes y diferencias entre las actividades económicas comparadas.',
    },
    {
      question: '¿Para qué me sirve comparar códigos CNAE o IAE?',
      answer:
        'Comparar códigos te puede ayudar en diversas situaciones:\n\n- Cuando estás decidiendo entre dos actividades económicas similares para tu negocio\n- Para entender las diferencias sutiles entre actividades relacionadas\n- Para analizar la evolución de una actividad económica entre diferentes versiones (en el caso de CNAE 2009 vs CNAE 2025)\n- Para tomar decisiones informadas sobre la clasificación fiscal de tu empresa',
    },
    {
      question: '¿Puedo guardar o compartir los resultados de la comparación?',
      answer:
        'Puedes descargar los resultados de la comparación en formato PDF utilizando el botón "Descargar PDF" que aparece junto a los resultados. También puedes utilizar la función de impresión de tu navegador o hacer capturas de pantalla si lo prefieres.',
    },
  ]

  useEffect(() => {
    // Set loading state
    setIsLoading(true)

    // Load comparison data from localStorage
    try {
      const storedData = localStorage.getItem('comparisonData')

      if (!storedData) {
        setError(
          'No se encontraron datos de comparación. Por favor, realiza una nueva comparación.'
        )
        setIsLoading(false)
        return
      }

      const parsedData = JSON.parse(storedData)

      // Check if we have the new format (arrays)
      if (parsedData.codes && parsedData.codeDetails && parsedData.codeType) {
        // New format
        if (parsedData.codes.length < 2 || parsedData.codeDetails.length < 2) {
          setError(
            'Datos de comparación incompletos. Por favor, realiza una nueva comparación.'
          )
          setIsLoading(false)
          return
        }

        setCodes(parsedData.codes)
        setCodeDetails(parsedData.codeDetails)
        setCodeType(parsedData.codeType)
        setComparisonPerformed(true)
      } else if (
        // Check if we have the old format (specific properties)
        parsedData.firstCode &&
        parsedData.secondCode &&
        parsedData.firstCodeDetails &&
        parsedData.secondCodeDetails &&
        parsedData.codeType
      ) {
        // Old format - convert to new format
        setCodes([parsedData.firstCode, parsedData.secondCode])
        setCodeDetails([
          parsedData.firstCodeDetails,
          parsedData.secondCodeDetails,
        ])
        setCodeType(parsedData.codeType)
        setComparisonPerformed(true)
      } else {
        setError(
          'Datos de comparación incompletos. Por favor, realiza una nueva comparación.'
        )
        setIsLoading(false)
        return
      }
    } catch (error) {
      console.error('Error loading comparison data:', error)
      setError(
        'Error al cargar los datos de comparación. Por favor, realiza una nueva comparación.'
      )
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Format description text with proper line breaks
  const formatText = (text) => {
    if (!text) return null

    // Split by line breaks and filter out empty lines
    const paragraphs = text.split('\n').filter((line) => line.trim() !== '')

    return paragraphs.map((paragraph, index) => (
      <p key={index} className='mb-3'>
        {paragraph}
      </p>
    ))
  }

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return `${date.getDate().toString().padStart(2, '0')}-${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}-${date.getFullYear()}`
  }

  // Get code type display name
  const getCodeTypeDisplayName = () => {
    switch (codeType) {
      case 'cnae':
        return 'CNAE'
      case 'iae':
        return 'IAE'
      default:
        return 'CNAE'
    }
  }

  // Get code type CSS class
  const getCodeTypeClass = () => {
    switch (codeType) {
      case 'cnae':
        return 'cnae'
      case 'cnae2009':
        return 'cnae2009'
      case 'iae':
        return 'iae'
      default:
        return 'cnae'
    }
  }

  // Get code value based on code type
  const getCodeValue = (code, details, property) => {
    if (code.type === 'cnae') {
      return details[`${property}_cnae`]
    } else if (code.type === 'cnae2009') {
      return details[`${property}_cnae_2009`]
    } else {
      return details[`${property}_iae`]
    }
  }

  // Handle new comparison button click
  const handleNewComparison = () => {
    navigate('/comparador-codigos/')
  }

  // Function to generate and download the PDF
  const handleDownloadPDF = async () => {
    // Mostrar indicador de carga
    setIsGeneratingPDF(true)

    try {
      // Capturar la tabla como imagen usando html2canvas-pro
      const tableElement = tableRef.current
      const canvas = await html2canvas(tableElement, {
        scale: 2, // Mayor calidad
        useCORS: true,
        logging: false,
      })

      // Crear nuevo documento PDF
      const pdf = new jsPDF('p', 'mm', 'a4')

      // Añadir título
      pdf.setFontSize(16)
      pdf.text(`Comparación de Códigos ${getCodeTypeDisplayName()}`, 14, 15)

      // Añadir fecha
      const today = new Date()
      pdf.setFontSize(10)
      pdf.text(`Generado el: ${formatDate(today.toISOString())}`, 14, 22)

      // Calcular dimensiones para ajustar la imagen al PDF
      const imgWidth = 190 // Ancho máximo en el PDF
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      // Añadir la imagen de la tabla al PDF
      pdf.addImage(
        canvas.toDataURL('image/png'),
        'PNG',
        10,
        30,
        imgWidth,
        imgHeight
      )

      // Guardar el PDF
      pdf.save(
        `comparacion_codigos_${getCodeTypeDisplayName().toLowerCase()}_${formatDate(today.toISOString())}.pdf`
      )
    } catch (error) {
      console.error('Error al generar el PDF:', error)
      // Mostrar mensaje de error
    } finally {
      // Ocultar indicador de carga
      setIsGeneratingPDF(false)
    }
  }

  // Check if any code is of type IAE
  const hasIaeCode = () => {
    return codes.some((code) => code.type === 'iae')
  }

  // Check if any code is of type CNAE or CNAE2009
  const hasCnaeCode = () => {
    return codes.some(
      (code) => code.type === 'cnae' || code.type === 'cnae2009'
    )
  }

  return (
    <div className='container mx-auto px-4'>
      {/* Header Section */}
      <header className='py-8 md:py-16 text-center'>
        <h1 className='text-2xl md:text-4xl font-bold mb-4 text-text-primary'>
          Resultados de Comparación de Códigos {getCodeTypeDisplayName()}
        </h1>
        <p className='text-text-secondary text-lg mb-8 max-w-2xl mx-auto'>
          Comparación detallada lado a lado de los códigos seleccionados. Esta
          herramienta te ayuda a identificar similitudes y diferencias entre
          actividades económicas.
        </p>
      </header>

      {/* Error Message */}
      {error && (
        <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mt-6 mb-6'>
          <p>{error}</p>
          <div className='mt-4 flex justify-center'>
            <button
              onClick={handleNewComparison}
              className='px-6 py-3 rounded-md text-white font-medium bg-primary hover:bg-opacity-90 transition-colors duration-300'
            >
              Realizar nueva comparación
            </button>
          </div>
        </div>
      )}

      {/* Loading Indicator */}
      {isLoading && (
        <div className='flex justify-center items-center py-12'>
          <div className='w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin'></div>
          <span className='ml-3 text-text-secondary'>
            Cargando resultados...
          </span>
        </div>
      )}

      {/* Comparison Results Section */}
      {!isLoading &&
        comparisonPerformed &&
        codes.length >= 2 &&
        codeDetails.length >= 2 && (
          <section className='py-4 md:py-8'>
            <div className='flex justify-between items-center mb-6'>
              <h2 className='text-xl md:text-3xl font-bold text-text-primary'>
                Resultados de la Comparación
              </h2>
              <div className='flex space-x-3'>
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className='px-4 py-2 rounded-md text-white font-medium bg-primary hover:bg-surface transition-colors duration-300 flex items-center'
                >
                  {isGeneratingPDF ? (
                    <>
                      <span className='w-4 h-4 border-2 border-surface border-t-transparent rounded-full animate-spin mr-2'></span>
                      Generando...
                    </>
                  ) : (
                    'Descargar PDF'
                  )}
                </button>
                <button
                  onClick={handleNewComparison}
                  className='px-4 py-2 rounded-md text-text-primary font-medium bg-secondary hover:bg-surface transition-colors duration-300'
                >
                  Volver a comparar
                </button>
              </div>
            </div>

            <div
              className='bg-surface rounded-lg p-6 overflow-x-auto'
              ref={tableRef}
            >
              <table className='w-full border-collapse bg-card'>
                <thead>
                  <tr className='bg-surface'>
                    <th className='p-4 text-left text-text-primary font-bold border-b border-border'>
                      Campo
                    </th>
                    {codes.map((code, index) => (
                      <th
                        key={index}
                        className='p-4 text-left text-text-primary font-bold border-b border-border'
                      >
                        {code.code} - {code.title}
                        <div className='text-xs font-normal mt-1'>
                          <span
                            className={`px-2 py-1 text-xs font-bold rounded bg-${code.type === 'cnae' ? 'cnae' : code.type === 'cnae2009' ? 'cnae2009' : 'iae'} text-white`}
                          >
                            {code.type === 'cnae'
                              ? 'CNAE 2025'
                              : code.type === 'cnae2009'
                                ? 'CNAE 2009'
                                : 'IAE'}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Code */}
                  <tr className='hover:bg-card'>
                    <td className='p-4 border-b border-border font-medium align-top'>
                      Código
                    </td>
                    {codes.map((code, index) => (
                      <td
                        key={index}
                        className='p-4 border-b border-border align-top'
                      >
                        {code.type === 'cnae'
                          ? codeDetails[index].cod_cnae
                          : code.type === 'cnae2009'
                            ? codeDetails[index].cod_cnae_2009
                            : codeDetails[index].cod_iae}
                      </td>
                    ))}
                  </tr>

                  {/* Full Code */}
                  <tr className='hover:bg-card'>
                    <td className='p-4 border-b border-border font-medium align-top'>
                      Código íntegro
                    </td>
                    {codes.map((code, index) => (
                      <td
                        key={index}
                        className='p-4 border-b border-border align-top'
                      >
                        {code.type === 'cnae'
                          ? codeDetails[index].cod_integro_cnae
                          : code.type === 'cnae2009'
                            ? codeDetails[index].cod_integro_cnae_2009
                            : codeDetails[index].cod_integro_iae}
                      </td>
                    ))}
                  </tr>

                  {/* Title */}
                  <tr className='hover:bg-card'>
                    <td className='p-4 border-b border-border font-medium align-top'>
                      Título
                    </td>
                    {codes.map((code, index) => (
                      <td
                        key={index}
                        className='p-4 border-b border-border align-top'
                      >
                        {code.type === 'cnae'
                          ? codeDetails[index].titulo_cnae
                          : code.type === 'cnae2009'
                            ? codeDetails[index].titulo_cnae_2009
                            : codeDetails[index].titulo_iae}
                      </td>
                    ))}
                  </tr>

                  {/* Description */}
                  <tr className='hover:bg-card'>
                    <td className='p-4 border-b border-border font-medium align-top'>
                      Descripción
                    </td>
                    {codes.map((code, index) => (
                      <td
                        key={index}
                        className='p-4 border-b border-border align-top'
                      >
                        {code.type === 'cnae'
                          ? formatText(codeDetails[index].descripcion_cnae)
                          : code.type === 'cnae2009'
                            ? formatText(
                                codeDetails[index].descripcion_cnae_2009
                              )
                            : formatText(codeDetails[index].descripcion_iae)}
                      </td>
                    ))}
                  </tr>

                  {/* Notes (only for CNAE) */}
                  {hasCnaeCode() && (
                    <tr className='hover:bg-card'>
                      <td className='p-4 border-b border-border font-medium align-top'>
                        Notas
                      </td>
                      {codes.map((code, index) => (
                        <td
                          key={index}
                          className='p-4 border-b border-border align-top'
                        >
                          {code.type === 'cnae'
                            ? formatText(codeDetails[index].notas_cnae)
                            : code.type === 'cnae2009'
                              ? formatText(codeDetails[index].notas_cnae_2009)
                              : 'No aplicable'}
                        </td>
                      ))}
                    </tr>
                  )}

                  {/* Category */}
                  <tr className='hover:bg-card'>
                    <td className='p-4 border-b border-border font-medium align-top'>
                      Categoría
                    </td>
                    {codes.map((code, index) => (
                      <td
                        key={index}
                        className='p-4 border-b border-border align-top'
                      >
                        {code.type === 'cnae'
                          ? codeDetails[index].categoria_cnae
                          : code.type === 'cnae2009'
                            ? codeDetails[index].categoria_cnae_2009
                            : codeDetails[index].categoria_iae}
                      </td>
                    ))}
                  </tr>

                  {/* Level */}
                  <tr className='hover:bg-card'>
                    <td className='p-4 border-b border-border font-medium align-top'>
                      Nivel
                    </td>
                    {codes.map((code, index) => (
                      <td
                        key={index}
                        className='p-4 border-b border-border align-top'
                      >
                        {code.type === 'cnae'
                          ? codeDetails[index].nivel_cnae
                          : code.type === 'cnae2009'
                            ? codeDetails[index].nivel_cnae_2009
                            : codeDetails[index].nivel_iae}
                      </td>
                    ))}
                  </tr>

                  {/* High Activity (only for IAE) */}
                  {hasIaeCode() && (
                    <tr className='hover:bg-card'>
                      <td className='p-4 border-b border-border font-medium align-top'>
                        Alta IAE
                      </td>
                      {codes.map((code, index) => (
                        <td
                          key={index}
                          className='p-4 border-b border-border align-top'
                        >
                          {code.type === 'iae'
                            ? codeDetails[index].alta_iae
                              ? 'Sí'
                              : 'No'
                            : 'No aplicable'}
                        </td>
                      ))}
                    </tr>
                  )}

                  {/* Keywords */}
                  <tr className='hover:bg-card'>
                    <td className='p-4 border-b border-border font-medium align-top'>
                      Palabras clave
                    </td>
                    {codes.map((code, index) => (
                      <td
                        key={index}
                        className='p-4 border-b border-border align-top'
                      >
                        {code.type === 'cnae' &&
                        codeDetails[index].palabras_claves_cnae
                          ? codeDetails[index].palabras_claves_cnae.map(
                              (keyword, keyIndex) => (
                                <span
                                  key={keyIndex}
                                  className='inline-block bg-card px-2 py-1 rounded text-sm mr-2 mb-2'
                                >
                                  {keyword}
                                </span>
                              )
                            )
                          : code.type === 'cnae2009' &&
                              codeDetails[index].palabras_claves_cnae_2009
                            ? codeDetails[index].palabras_claves_cnae_2009.map(
                                (keyword, keyIndex) => (
                                  <span
                                    key={keyIndex}
                                    className='inline-block bg-card px-2 py-1 rounded text-sm mr-2 mb-2'
                                  >
                                    {keyword}
                                  </span>
                                )
                              )
                            : code.type === 'iae' &&
                                codeDetails[index].palabras_claves_iae
                              ? codeDetails[index].palabras_claves_iae.map(
                                  (keyword, keyIndex) => (
                                    <span
                                      key={keyIndex}
                                      className='inline-block bg-card px-2 py-1 rounded text-sm mr-2 mb-2'
                                    >
                                      {keyword}
                                    </span>
                                  )
                                )
                              : null}
                      </td>
                    ))}
                  </tr>

                  {/* Source */}
                  <tr className='hover:bg-card'>
                    <td className='p-4 border-b border-border font-medium align-top'>
                      Fuente
                    </td>
                    {codes.map((code, index) => (
                      <td
                        key={index}
                        className='p-4 border-b border-border align-top'
                      >
                        {code.type === 'cnae'
                          ? codeDetails[index].fuente_cnae
                          : code.type === 'cnae2009'
                            ? codeDetails[index].fuente_cnae_2009
                            : codeDetails[index].fuente_iae}
                      </td>
                    ))}
                  </tr>

                  {/* Last Update */}
                  <tr className='hover:bg-card'>
                    <td className='p-4 border-b border-border font-medium align-top'>
                      Última actualización
                    </td>
                    {codes.map((code, index) => (
                      <td
                        key={index}
                        className='p-4 border-b border-border align-top'
                      >
                        {code.type === 'cnae'
                          ? formatDate(
                              codeDetails[index].ultima_actualizacion_cnae
                            )
                          : code.type === 'cnae2009'
                            ? formatDate(
                                codeDetails[index]
                                  .ultima_actualizacion_cnae_2009
                              )
                            : formatDate(
                                codeDetails[index].ultima_actualizacion_iae
                              )}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

      {/* FAQ Section */}
      <Faq
        title='Preguntas Frecuentes sobre el Comparador de Códigos'
        description='Resolvemos tus dudas sobre cómo utilizar el comparador de códigos y cómo puede ayudarte a tomar decisiones informadas sobre la clasificación de tu actividad económica.'
        faqData={faqData}
      />
    </div>
  )
}

export default ComparisonResultsPage
